<template>
  <div class="form-step">
    <div class="section-header">
      <h5 class="section-title">EDCTP Fellow Information</h5>
    </div>
    <div class="row mb-3">
      <div class="col-md-6">
        <label class="form-label">EDCTP Fellow</label>
        <input
          type="text"
          class="form-control"
          v-model="fellowship.fellow"
          @input="emitUpdate"
          placeholder="Enter fellow name"
          readonly
          style="background: #e9ecef"
        >
      </div>
      <div class="col-md-6">
        <label class="form-label">Gender</label>
        <select
          class="form-select"
          v-model="fellowship.gender"
          @change="emitUpdate"
          disabled
          style="background: #e9ecef"
        >
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="other">Other</option>
        </select>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-4">
        <label class="form-label">EDCTP Programme</label>
        <input
          type="text"
          class="form-control"
          :value="programmeLabel"
          readonly
          style="background: #e9ecef"
        />
      </div>
      <div class="col-md-4">
        <label class="form-label">EDCTP Call</label>
        <input
          type="text"
          class="form-control"
          :value="callLabel"
          readonly
          style="background: #e9ecef"
        />
      </div>
      <div class="col-md-4">
        <label class="form-label">EDCTP Call Year</label>
        <input
          type="text"
          class="form-control"
          :value="callYearLabel"
          placeholder="Enter call year"
          readonly
          style="background: #e9ecef"
        >
      </div>
    </div>
    <div class="section-header">
      <h5 class="section-title">Fellowship Information</h5>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label class="form-label">EDCTP Title</label>
        <input
          type="text"
          class="form-control"
          v-model="fellowship.title"
          @input="emitUpdate"
          placeholder="Enter project title"
          readonly
          style="background: #e9ecef"
        >
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-4">
        <label class="form-label">EDCTP Project Code</label>
        <input
          type="text"
          class="form-control"
          v-model="fellowship.projectCode"
          @input="emitUpdate"
          placeholder="Enter project code"
          readonly
          style="background: #e9ecef"
        >
      </div>
      <div class="col-md-4">
        <label class="form-label">Start Date</label>
        <input
          type="date"
          class="form-control"
          v-model="fellowship.startDate"
          @input="emitUpdate"
          readonly
          style="background: #e9ecef"
        >
      </div>
      <div class="col-md-4">
        <label class="form-label">End Date</label>
        <input
          type="date"
          class="form-control"
          v-model="fellowship.endDate"
          @input="emitUpdate"
          readonly
          style="background: #e9ecef"
        >
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-4">
        <label class="form-label">Objectives</label>
        <textarea
          class="form-control"
          rows="4"
          v-model="fellowship.objectives"
          @input="emitUpdate"
          placeholder="Enter objectives..."
        ></textarea>
      </div>
      <div class="col-md-4">
        <label class="form-label">Study Design</label>
        <textarea
          class="form-control"
          rows="4"
          v-model="fellowship.studyDesign"
          @input="emitUpdate"
          placeholder="Enter study design..."
        ></textarea>
      </div>
      <div class="col-md-4">
        <label class="form-label">Project Summary</label>
        <textarea
          class="form-control"
          rows="4"
          v-model="fellowship.projectSummary"
          @input="emitUpdate"
          placeholder="Enter project summary..."
        ></textarea>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-4">
        <label class="form-label">Fellowship Status</label>
        <select
          class="form-select"
          v-model="fellowship.status"
          @change="emitUpdate"
        >
          <option value="">Select Fellowship Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
      </div>
    </div>

    <!-- Collapsible Host Organisation Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title mb-0">Host Organisation</h5>
      <button type="button" class="btn btn-outline-primary" @click="addCollaborator">
        <i class="bi bi-plus me-2"></i>Add Organisation
      </button>
    </div>
    <div v-if="collaboratorDraft" class="card mb-3 border-info">
      <div class="card-body">
        <div class="row">
          <div class="col-md-4">
            <label class="form-label">Department</label>
            <input
              ref="newCollaboratorDepartmentInput"
              type="text"
              class="form-control"
              :class="{ 'field-invalid': isCollaboratorFieldInvalid(collaboratorDraft, 'department', null, true) }"
              v-model="collaboratorDraft.department"
              @input="handleCollaboratorDraftInput"
              placeholder="Enter department"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Institution</label>
            <input
              type="text"
              class="form-control"
              :class="{ 'field-invalid': isCollaboratorFieldInvalid(collaboratorDraft, 'institution', null, true) }"
              v-model="collaboratorDraft.institution"
              @input="handleCollaboratorDraftInput"
              placeholder="Enter institution"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Country</label>
            <select class="form-select" :class="{ 'field-invalid': isCollaboratorFieldInvalid(collaboratorDraft, 'country', null, true) }" v-model="collaboratorDraft.country" @change="handleCollaboratorDraftInput">
              <option value="">Select Country</option>
              <option v-for="country in internationalCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewCollaborator">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="saveNewCollaborator"
          >
            Save
          </button>
        </div>
      </div>
    </div>
    <!-- Collapsible Host Organisation Section (DRY, reusable) -->
    <div v-for="(collaborator, index) in collaborators" :key="'collab-' + index" class="accordion mb-3">
      <CollapsibleItem
        :expanded="expandedCollaborator === index"
        :panelId="'collab-collapse-' + index"
        :headerId="'collab-header-' + index"
        @toggle="expandedCollaborator = expandedCollaborator === index ? null : index"
      >
        <template #header>
          {{ formatCollapsibleHeaderWithCountry([collaborator.institution, collaborator.department], collaborator.country, `New Host Organisation ${index + 1}`) }}
        </template>
        <div class="row">
          <div class="col-md-4">
            <label class="form-label">Department</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isCollaboratorFieldInvalid(collaborator, 'department', index) }" v-model="collaborator.department" @input="markCollaboratorDirty(index)" placeholder="Enter department">
          </div>
          <div class="col-md-4">
            <label class="form-label">Institution</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isCollaboratorFieldInvalid(collaborator, 'institution', index) }" v-model="collaborator.institution" @input="markCollaboratorDirty(index)" placeholder="Enter institution">
          </div>
          <div class="col-md-4">
            <label class="form-label">Country</label>
            <select class="form-select" :class="{ 'field-invalid': isCollaboratorFieldInvalid(collaborator, 'country', index) }" v-model="collaborator.country" @change="markCollaboratorDirty(index)">
              <option value="">Select Country</option>
              <option v-for="country in internationalCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="!collaboratorDirty[index]"
            @click="saveCollaborator(index)"
          >
            Save
          </button>
          <button
            type="button"
            class="btn btn-outline-danger"
            @click="removeCollaborator(index)"
          >
            Remove
          </button>
        </div>
      </CollapsibleItem>
    </div>

    <!-- Collapsible Participants Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title mb-0">Participating Institution</h5>
      <button type="button" class="btn btn-outline-primary" @click="addParticipant">
        <i class="bi bi-plus me-2"></i>Add Participant
      </button>
    </div>
    <div v-if="participantDraft" class="card mb-3 border-info">
      <div class="card-body">
        <div class="row">
          <div class="col-md-4">
            <label class="form-label">Department</label>
            <input
              ref="newParticipantDepartmentInput"
              type="text"
              class="form-control"
              :class="{ 'field-invalid': isParticipantFieldInvalid(participantDraft, 'department', null, true) }"
              v-model="participantDraft.department"
              @input="handleParticipantDraftInput"
              placeholder="Enter department"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Institution</label>
            <input
              type="text"
              class="form-control"
              :class="{ 'field-invalid': isParticipantFieldInvalid(participantDraft, 'institution', null, true) }"
              v-model="participantDraft.institution"
              @input="handleParticipantDraftInput"
              placeholder="Enter institution"
            >
          </div>
          <div class="col-md-4">
            <label class="form-label">Country</label>
            <select class="form-select" :class="{ 'field-invalid': isParticipantFieldInvalid(participantDraft, 'country', null, true) }" v-model="participantDraft.country" @change="handleParticipantDraftInput">
              <option value="">Select Country</option>
              <option v-for="country in internationalCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewParticipant">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="saveNewParticipant"
          >
            Save
          </button>
        </div>
      </div>
    </div>
    <!-- Collapsible Participants Section (DRY, reusable) -->
    <div v-for="(participant, index) in participants" :key="'part-' + index" class="accordion mb-3">
      <CollapsibleItem
        :expanded="expandedParticipant === index"
        :panelId="'part-collapse-' + index"
        :headerId="'part-header-' + index"
        @toggle="expandedParticipant = expandedParticipant === index ? null : index"
      >
        <template #header>
          {{ formatCollapsibleHeaderWithCountry([participant.institution, participant.department], participant.country, `New Participant ${index + 1}`) }}
        </template>
        <div class="row">
          <div class="col-md-4">
            <label class="form-label">Department</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isParticipantFieldInvalid(participant, 'department', index) }" v-model="participant.department" @input="markParticipantDirty(index)" placeholder="Enter department">
          </div>
          <div class="col-md-4">
            <label class="form-label">Institution</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isParticipantFieldInvalid(participant, 'institution', index) }" v-model="participant.institution" @input="markParticipantDirty(index)" placeholder="Enter institution">
          </div>
          <div class="col-md-4">
            <label class="form-label">Country</label>
            <select class="form-select" :class="{ 'field-invalid': isParticipantFieldInvalid(participant, 'country', index) }" v-model="participant.country" @change="markParticipantDirty(index)">
              <option value="">Select Country</option>
              <option v-for="country in internationalCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="!participantDirty[index]"
            @click="saveParticipant(index)"
          >
            Save
          </button>
          <button
            type="button"
            class="btn btn-outline-danger"
            @click="removeParticipant(index)"
          >
            Remove
          </button>
        </div>
      </CollapsibleItem>
    </div>

    <!-- Collapsible Students Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title mb-0">Students Supervised</h5>
      <button type="button" class="btn btn-outline-primary" @click="addStudent">
        <i class="bi bi-plus me-2"></i>Add Student
      </button>
    </div>
    <div v-if="studentDraft" class="card mb-3 border-info">
      <div class="card-body">
        <div class="row">
          <div class="col-md-3">
            <label class="form-label">Level of Study</label>
            <input ref="newStudentTypeInput" type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(studentDraft, 'type', null, true) }" v-model="studentDraft.type" @input="handleStudentDraftInput" placeholder="Enter type">
          </div>
          <div class="col-md-3">
            <label class="form-label">Name</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(studentDraft, 'name', null, true) }" v-model="studentDraft.name" @input="handleStudentDraftInput" placeholder="Enter student name">
          </div>
          <div class="col-md-3">
            <label class="form-label">Title</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(studentDraft, 'title', null, true) }" v-model="studentDraft.title" @input="handleStudentDraftInput" placeholder="Enter title">
          </div>
          <div class="col-md-3">
            <label class="form-label">Country</label>
            <select class="form-select" :class="{ 'field-invalid': isStudentFieldInvalid(studentDraft, 'country', null, true) }" v-model="studentDraft.country" @change="handleStudentDraftInput">
              <option value="">Select Country</option>
              <option v-for="country in africanCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-6">
            <label class="form-label">University</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(studentDraft, 'university', null, true) }" v-model="studentDraft.university" @input="handleStudentDraftInput" placeholder="Enter university name">
          </div>
          <div class="col-md-3">
            <label class="form-label">Start Date</label>
            <input type="date" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(studentDraft, 'startDate', null, true) }" v-model="studentDraft.startDate" @input="handleStudentDraftInput">
          </div>
          <div class="col-md-3">
            <label class="form-label">End Date</label>
            <input type="date" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(studentDraft, 'endDate', null, true) }" v-model="studentDraft.endDate" @input="handleStudentDraftInput">
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewStudent">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="saveNewStudent">
            Save
          </button>
        </div>
      </div>
    </div>
    <!-- Collapsible Students Section (DRY, reusable) -->
    <div v-for="(student, index) in students" :key="'student-' + index" class="accordion mb-3">
      <CollapsibleItem
        :expanded="expandedStudent === index"
        :panelId="'student-collapse-' + index"
        :headerId="'student-header-' + index"
        @toggle="expandedStudent = expandedStudent === index ? null : index"
      >
        <template #header>
          {{ formatCollapsibleHeader([student.university, student.type, student.name, getYear(student.startDate)], `New Student ${index + 1}`) }}
        </template>
        <div class="row">
          <div class="col-md-3">
            <label class="form-label">Type</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(student, 'type', index) }" v-model="student.type" @input="markStudentDirty(index)" placeholder="Enter type">
          </div>
          <div class="col-md-3">
            <label class="form-label">Name</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(student, 'name', index) }" v-model="student.name" @input="markStudentDirty(index)" placeholder="Enter student name">
          </div>
          <div class="col-md-3">
            <label class="form-label">Title</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(student, 'title', index) }" v-model="student.title" @input="markStudentDirty(index)" placeholder="Enter title">
          </div>
          <div class="col-md-3">
            <label class="form-label">Country</label>
            <select class="form-select" :class="{ 'field-invalid': isStudentFieldInvalid(student, 'country', index) }" v-model="student.country" @change="markStudentDirty(index)">
              <option value="">Select Country</option>
              <option v-for="country in africanCountries" :key="country.value" :value="country.value">
                {{ country.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-6">
            <label class="form-label">University</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(student, 'university', index) }" v-model="student.university" @input="markStudentDirty(index)" placeholder="Enter university name">
          </div>
          <div class="col-md-3">
            <label class="form-label">Start Date</label>
            <input type="date" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(student, 'startDate', index) }" v-model="student.startDate" @input="markStudentDirty(index)">
          </div>
          <div class="col-md-3">
            <label class="form-label">End Date</label>
            <input type="date" class="form-control" :class="{ 'field-invalid': isStudentFieldInvalid(student, 'endDate', index) }" v-model="student.endDate" @input="markStudentDirty(index)">
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="!studentDirty[index]"
            @click="saveStudent(index)"
          >
            Save
          </button>
          <button
            type="button"
            class="btn btn-outline-danger"
            @click="removeStudent(index)"
          >
            Remove
          </button>
        </div>
      </CollapsibleItem>
    </div>

    <!-- Collapsible Sites Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title mb-0">Sites</h5>
      <button type="button" class="btn btn-outline-primary" @click="addSite">
        <i class="bi bi-plus me-2"></i>Add Site
      </button>
    </div>
    <div v-if="siteDraft" class="card mb-3 border-info">
      <div class="card-body">
        <div class="row">
          <div class="col-md-12">
            <label class="form-label">Name</label>
            <input ref="newSiteNameInput" type="text" class="form-control" :class="{ 'field-invalid': isSiteFieldInvalid(siteDraft, null, true) }" v-model="siteDraft.name" @input="handleSiteDraftInput" placeholder="Enter site name">
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewSite">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="saveNewSite">
            Save
          </button>
        </div>
      </div>
    </div>
    <!-- Collapsible Sites Section (DRY, reusable) -->
    <div v-for="(site, index) in sites" :key="'site-' + index" class="accordion mb-3">
      <CollapsibleItem
        :expanded="expandedSite === index"
        :panelId="'site-collapse-' + index"
        :headerId="'site-header-' + index"
        @toggle="expandedSite = expandedSite === index ? null : index"
      >
        <template #header>
          {{ formatCollapsibleHeader([site.name], `New Site ${index + 1}`) }}
        </template>
        <div class="row">
          <div class="col-md-12">
            <label class="form-label">Name</label>
            <input type="text" class="form-control" :class="{ 'field-invalid': isSiteFieldInvalid(site, index) }" v-model="site.name" @input="markSiteDirty(index)" placeholder="Enter site name">
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="!siteDirty[index]"
            @click="saveSite(index)"
          >
            Save
          </button>
          <button
            type="button"
            class="btn btn-outline-danger"
            @click="removeSite(index)"
          >
            Remove
          </button>
        </div>
      </CollapsibleItem>
    </div>

    <!-- PhD Study Section -->
    <div class="section-header">
      <h5 class="section-title">Results & Outcomes</h5>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <label class="form-label d-none">Results & Outcomes</label>
        <textarea
          class="form-control"
          :class="{ 'field-invalid': isPhdFieldInvalid('resultsAndOutcomes') }"
          rows="4"
          v-model="fellowship.resultsAndOutcomes"
          @input="markPhdDirty"
        ></textarea>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12 d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="!phdDirty"
          @click="savePhdStudy"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>

// Import the reusable CollapsibleItem component
import CollapsibleItem from '@/components/common/CollapsibleItem.vue'
import { computed, ref, watch, nextTick } from 'vue'
import { parseDate } from '@/utils/dateUtils'
import { getCountryName, normalizeCountryValueToCode } from '@/utils/countryMap'

export default {
  name: 'FellowshipForm',
  // Register CollapsibleItem for use in template
  components: { CollapsibleItem },
  props: {
    profileData: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    programmeLabel() {
      const value = this.fellowship.programme;
      if (value === null || value === undefined || value === "") return "";

      // Values are stored as a plain number (or a numeric string such as
      // "1", "2", "3", ...) - one EDCTP programme generation per number,
      // so this works for any future EDCTP4, EDCTP5, etc. without changes.
      const match = String(value).match(/\d+/);
      if (match) {
        if (match[0] === "3") return "Global Health EDCTP3";
        return `EDCTP ${match[0]}`;
      }

      return String(value);
    },
    callLabel() {
      const map = {
        1: "Career Development Fellowship (CDF)",
        2: "Senior Fellowship (SF)",
        3: "EDCTP Clinical Research & Development Fellowship (R&D F)",
        4: "EDCTP-AREF Preparatory Fellowships (PF)",
        5: "Senior Fellowship Plus (SFP)",
        6: "Disease Outbreak and Epidemic Response (EPI Fellows)",
        7: "Female PhD Fellowships",
        8: "EDCTP Master's Fellowship"
      };

      // Ensure key is treated as number OR string
      const key = this.fellowship.call;

      return map[key] || "";
    },
    callYearLabel() {
      const raw = this.fellowship.callDate
      if (!raw) return ''

      const parsed = new Date(raw)
      if (!isNaN(parsed.getTime())) {
        return String(parsed.getFullYear())
      }

      const rawString = String(raw)
      const yearMatch = rawString.match(/\b(19|20)\d{2}\b/)
      return yearMatch ? yearMatch[0] : rawString
    }
  },

  emits: ['update'],

  setup(props, { emit }) {
    const fellowshipId = ref(null)

    const phdDirty = ref(false)
    const phdAttempted = ref(false)

    const internationalCountryCodes = 'AF,AX,AL,DZ,AS,AD,AO,AI,AQ,AG,AR,AM,AW,AU,AT,AZ,BS,BH,BD,BB,BY,BE,BZ,BJ,BM,BT,BO,BQ,BA,BW,BV,BR,IO,BN,BG,BF,BI,CV,KH,CM,CA,KY,CF,TD,CL,CN,CX,CC,CO,KM,CG,CD,CK,CR,CI,HR,CU,CW,CY,CZ,DK,DJ,DM,DO,EC,EG,SV,GQ,ER,EE,SZ,ET,FK,FO,FJ,FI,FR,GF,PF,TF,GA,GM,GE,DE,GH,GI,GR,GL,GD,GP,GU,GT,GG,GN,GW,GY,HT,HM,VA,HN,HK,HU,IS,IN,ID,IR,IQ,IE,IM,IL,IT,JM,JP,JE,JO,KZ,KE,KI,KP,KR,KW,KG,LA,LV,LB,LS,LR,LY,LI,LT,LU,MO,MG,MW,MY,MV,ML,MT,MH,MQ,MR,MU,YT,MX,FM,MD,MC,MN,ME,MS,MA,MZ,MM,NA,NR,NP,NL,NC,NZ,NI,NE,NG,NU,NF,MK,MP,NO,OM,PK,PW,PS,PA,PG,PY,PE,PH,PN,PL,PT,PR,QA,RE,RO,RU,RW,BL,SH,KN,LC,MF,PM,VC,WS,SM,ST,SA,SN,RS,SC,SL,SG,SX,SK,SI,SB,SO,ZA,GS,SS,ES,LK,SD,SR,SJ,SE,CH,SY,TW,TJ,TZ,TH,TL,TG,TK,TO,TT,TN,TR,TM,TC,TV,UG,UA,AE,GB,UM,US,UY,UZ,VU,VE,VN,VG,VI,WF,EH,YE,ZM,ZW'.split(',')
    const regionNames = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
      ? new Intl.DisplayNames(['en'], { type: 'region' })
      : null
    const internationalCountries = ref(
      internationalCountryCodes
        .map((value) => ({ value, label: regionNames?.of(value) || value }))
        .sort((left, right) => left.label.localeCompare(right.label))
    )

    const africanCountries = ref([
      { value: 'DZ', label: 'Algeria' },
      { value: 'AO', label: 'Angola' },
      { value: 'BJ', label: 'Benin' },
      { value: 'BW', label: 'Botswana' },
      { value: 'BF', label: 'Burkina Faso' },
      { value: 'BI', label: 'Burundi' },
      { value: 'CM', label: 'Cameroon' },
      { value: 'CV', label: 'Cape Verde' },
      { value: 'CF', label: 'Central African Republic' },
      { value: 'TD', label: 'Chad' },
      { value: 'KM', label: 'Comoros' },
      { value: 'CG', label: 'Congo' },
      { value: 'CD', label: 'Democratic Republic of Congo' },
      { value: 'DJ', label: 'Djibouti' },
      { value: 'EG', label: 'Egypt' },
      { value: 'GQ', label: 'Equatorial Guinea' },
      { value: 'ER', label: 'Eritrea' },
      { value: 'SZ', label: 'Eswatini' },
      { value: 'ET', label: 'Ethiopia' },
      { value: 'GA', label: 'Gabon' },
      { value: 'GM', label: 'Gambia' },
      { value: 'GH', label: 'Ghana' },
      { value: 'GN', label: 'Guinea' },
      { value: 'GW', label: 'Guinea-Bissau' },
      { value: 'CI', label: 'Ivory Coast' },
      { value: 'KE', label: 'Kenya' },
      { value: 'LS', label: 'Lesotho' },
      { value: 'LR', label: 'Liberia' },
      { value: 'LY', label: 'Libya' },
      { value: 'MG', label: 'Madagascar' },
      { value: 'MW', label: 'Malawi' },
      { value: 'ML', label: 'Mali' },
      { value: 'MR', label: 'Mauritania' },
      { value: 'MU', label: 'Mauritius' },
      { value: 'MA', label: 'Morocco' },
      { value: 'MZ', label: 'Mozambique' },
      { value: 'NA', label: 'Namibia' },
      { value: 'NE', label: 'Niger' },
      { value: 'NG', label: 'Nigeria' },
      { value: 'RW', label: 'Rwanda' },
      { value: 'ST', label: 'São Tomé and Príncipe' },
      { value: 'SN', label: 'Senegal' },
      { value: 'SC', label: 'Seychelles' },
      { value: 'SL', label: 'Sierra Leone' },
      { value: 'SO', label: 'Somalia' },
      { value: 'ZA', label: 'South Africa' },
      { value: 'SS', label: 'South Sudan' },
      { value: 'SD', label: 'Sudan' },
      { value: 'TZ', label: 'Tanzania' },
      { value: 'TG', label: 'Togo' },
      { value: 'TN', label: 'Tunisia' },
      { value: 'UG', label: 'Uganda' },
      { value: 'ZM', label: 'Zambia' },
      { value: 'ZW', label: 'Zimbabwe' }
    ])

    // ---------- LOCAL STATE ----------
    const fellowship = ref({
      fellow: '',
      gender: '',
      programme: '',
      call: '',
      callDate: '',
      title: '',
      projectCode: '',
      startDate: '',
      endDate: '',
      status: '',
      objectives: '',
      studyDesign: '',
      projectSummary: '',
      resultsAndOutcomes: ''
    })

    const collaborators = ref([])
    const participants = ref([])
    const students = ref([])
    const sites = ref([])

    const phdStudy = ref({
      title: '',
      university: '',
      startDate: '',
      endDate: ''
    })

    const isInitializing = ref(false)
    const newCollaboratorDepartmentInput = ref(null)
    const collaboratorDraft = ref(null)
    const collaboratorDraftAttempted = ref(false)
    const collaboratorDirty = ref({})
    const collaboratorAttempted = ref({})
    const newParticipantDepartmentInput = ref(null)
    const participantDraft = ref(null)
    const participantDraftAttempted = ref(false)
    const participantDirty = ref({})
    const participantAttempted = ref({})
    const newStudentTypeInput = ref(null)
    const studentDraft = ref(null)
    const studentDraftAttempted = ref(false)
    const studentDirty = ref({})
    const studentAttempted = ref({})
    const newSiteNameInput = ref(null)
    const siteDraft = ref(null)
    const siteDraftAttempted = ref(false)
    const siteDirty = ref({})
    const siteAttempted = ref({})
    let debounceTimer = null

    const markPhdDirty = () => {
      phdDirty.value = true
    }

    const isPhdValid = computed(() => {
      return Boolean(
        String(fellowship.value.resultsAndOutcomes || '').trim()
      )
    })

    const savePhdStudy = () => {
      if (!isPhdValid.value) {
        phdAttempted.value = true
        return
      }

      emitUpdate()
      phdDirty.value = false
      phdAttempted.value = false
    }

    const isPhdFieldInvalid = (field) => {
      if (!phdAttempted.value) return false

      if (field === 'resultsAndOutcomes') return !String(fellowship.value.resultsAndOutcomes || '').trim()
      return false
    }

    const formatDate = (value) => {
      if (!value) return ''
      const d = new Date(value)
      return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]
    }

    const normalizeCountryToCode = (countryValue) => {
      // Use shared utility function with both country lists
      return normalizeCountryValueToCode(countryValue, [
        internationalCountries.value,
        africanCountries.value
      ])
    }

    const normaliseArray = (arr, emptyTemplate, options = {}) => {
      const { fallbackToEmptyTemplate = true } = options

      if (!Array.isArray(arr)) {
        return fallbackToEmptyTemplate ? [{ ...emptyTemplate }] : []
      }

      const cleaned = arr
        .filter(item => item && typeof item === 'object')
        .map(item => ({ ...emptyTemplate, ...item }))
        .filter(item =>
          Object.values(item ?? {}).some(
            v => v !== '' && v !== null && v !== undefined
          )
        )

      return cleaned.length ? cleaned : (fallbackToEmptyTemplate ? [{ ...emptyTemplate }] : [])
    }

    const formatCollapsibleHeader = (parts, fallbackLabel) => {
      const normalized = (Array.isArray(parts) ? parts : [])
        .map((part) => (part == null ? '' : String(part).trim()))
        .filter(Boolean)

      return normalized.length ? normalized.join(' | ') : fallbackLabel
    }

    const formatCollapsibleHeaderWithCountry = (parts, countryValue, fallbackLabel) => {
      // Normalize country code to name, keep it separate to avoid false positives
      const countryName = countryValue ? getCountryName(countryValue) || countryValue : ''

      // Replace the last part (country) with the normalized name
      const partsWithCountry = countryValue
        ? [...parts.slice(0, -1), countryName]
        : parts

      return formatCollapsibleHeader(partsWithCountry, fallbackLabel)
    }

    const getYear = (value) => {
      if (value === null || value === undefined) return ''
      const asString = String(value).trim()
      const match = asString.match(/^(\d{4})/)
      return match ? match[1] : ''
    }

    const isCollaboratorValid = (item) => {
      if (!item) return false

      return Boolean(
        String(item.department || '').trim() &&
        String(item.institution || '').trim() &&
        String(item.country || '').trim()
      )
    }

    const isParticipantValid = (item) => {
      if (!item) return false

      return Boolean(
        String(item.department || '').trim() &&
        String(item.institution || '').trim() &&
        String(item.country || '').trim()
      )
    }

    const hasInvalidDateRange = (item) => {
      if (!item?.startDate || !item?.endDate) return false
      return new Date(item.startDate) > new Date(item.endDate)
    }

    const isStudentValid = (item) => {
      if (!item) return false

      return Boolean(
        String(item.type || '').trim() &&
        String(item.name || '').trim() &&
        String(item.title || '').trim() &&
        String(item.country || '').trim() &&
        String(item.university || '').trim() &&
        String(item.startDate || '').trim() &&
        String(item.endDate || '').trim() &&
        !hasInvalidDateRange(item)
      )
    }

    const isSiteValid = (item) => {
      if (!item) return false
      return Boolean(String(item.name || '').trim())
    }

    // ---------- SYNC FROM PARENT ----------
    const syncFromProps = async (newVal) => {
      if (!newVal) {
        fellowshipId.value = null
        return
      }

      fellowshipId.value = newVal.fellowship_id || null

      isInitializing.value = true

      const userName =
        props.profileData?.user?.name ||
        props.profileData?.personal?.fullName ||
        props.profileData?.name ||
        newVal?.fellow ||
        ''

      const rawGender = String(newVal.gender || '').trim()
      const normalizedGender = rawGender.toLowerCase()
      const genderValue = normalizedGender === 'male'
        ? 'Male'
        : normalizedGender === 'female'
          ? 'Female'
          : normalizedGender === 'other'
            ? 'other'
            : rawGender

      const rawStatus = String(newVal.status || '').trim()
      const normalizedStatus = rawStatus.toLowerCase()
      const statusValue = normalizedStatus === '1' || normalizedStatus === 'active'
        ? 'active'
        : normalizedStatus === '2' || normalizedStatus === 'completed'
          ? 'completed'
          : rawStatus

      fellowship.value = {
        fellow: userName,
        gender: genderValue,
        programme: newVal.programme || '',
        call: newVal.call || '',
        callDate: formatDate(newVal.callDate),
        title: newVal.title || '',
        projectCode: newVal.projectCode || newVal.edctp_project_code || newVal.edctp_project || '',
        startDate: formatDate(newVal.startDate),
        endDate: formatDate(newVal.endDate),
        status: statusValue,
        objectives: newVal.objectives || '',
        studyDesign: newVal.studyDesign || '',
        projectSummary: newVal.projectSummary || '',
        resultsAndOutcomes: newVal.resultsAndOutcomes || ''
      }

      // Collaborators - normalize country names to codes
      const incoming = normaliseArray(newVal.collaborators, {
        department: '', institution: '', country: ''
      }, { fallbackToEmptyTemplate: false }).map(collab => ({
        ...collab,
        country: normalizeCountryToCode(collab.country)
      }))

      collaborators.value = incoming
      collaboratorDirty.value = {}

      // Participants - normalize country names to codes
      const incomingParticipants = normaliseArray(newVal.participants, {
        department: '', institution: '', country: ''
      }, { fallbackToEmptyTemplate: false }).map(part => ({
        ...part,
        country: normalizeCountryToCode(part.country)
      }))
      participants.value = incomingParticipants
      participantDirty.value = {}

      // Students - normalize country names to codes
      const incomingStudents = normaliseArray(newVal.students, {
        type: '', name: '', title: '', country: '', university: '', startDate: '', endDate: ''
      }, { fallbackToEmptyTemplate: false }).map(student => ({
        ...student,
        country: normalizeCountryToCode(student.country)
      }))
      // Parse dates from both snake_case and camelCase field names
      students.value = incomingStudents.map(student => ({
        ...student,
        startDate: parseDate(student.start_date || student.startDate || '', false),
        endDate: parseDate(student.end_date || student.endDate || '', true)
      }))
      studentDirty.value = {}

      // Sites
      const incomingSites = normaliseArray(newVal.sites, { name: '' })
      sites.value = incomingSites.filter((site) => String(site.name || '').trim() !== '')
      siteDirty.value = {}

      const phdArray = newVal.phdStudy || newVal.phd_study || []

      const phdSource = Array.isArray(phdArray) && phdArray.length
        ? phdArray[0]
        : {}

      if (!fellowship.value.status && phdSource.status) {
        fellowship.value.status = String(phdSource.status)
      }

      // ⛔ DO NOT overwrite PhD while user is editing
      if (phdDirty.value) return

      phdStudy.value = {
        title: phdSource.title || '',
        university: phdSource.university || '',
        startDate: formatDate(phdSource.startDate ?? phdSource.start_date),
        endDate: formatDate(phdSource.endDate ?? phdSource.end_date),
        subjects: phdSource.subjects || '',
        results: phdSource.results || ''
      }

      await nextTick()
      isInitializing.value = false
    }

    watch(
      () => props.formData,
      (newVal) => {
        // Sync props from parent when formData changes
        syncFromProps(newVal)
      },
      { immediate: true }
    )

    // ---------- EMIT TO PARENT ----------
    const emitUpdate = () => {
      if (isInitializing.value) return

      if (debounceTimer) clearTimeout(debounceTimer)

      debounceTimer = setTimeout(() => {
        const filterEmpty = (arr) =>
          arr.filter((item) =>
            Object.values(item ?? {}).some(
              (v) => v !== '' && v !== null && v !== undefined
            )
          )

        const hasPhDData = Object.values(phdStudy.value).some(
          (v) => v !== '' && v !== null && v !== undefined
        )

        const payload = {
          fellowship: {
            fellowship_id: fellowshipId.value,
            fellow: fellowship.value.fellow || '',
            gender: fellowship.value.gender || '',
            programme: fellowship.value.programme || '',
            call: fellowship.value.call || '',
            callDate: fellowship.value.callDate || '',
            title: fellowship.value.title || '',
            projectCode: fellowship.value.projectCode || '',
            startDate: fellowship.value.startDate || '',
            endDate: fellowship.value.endDate || '',
            status: fellowship.value.status || '',
            objectives: fellowship.value.objectives || '',
            studyDesign: fellowship.value.studyDesign || '',
            projectSummary: fellowship.value.projectSummary || '',
            collaborators: filterEmpty(collaborators.value),
            participants: filterEmpty(participants.value),
            students: filterEmpty(students.value),
            sites: filterEmpty(sites.value),
            resultsAndOutcomes: fellowship.value.resultsAndOutcomes,
            phd_study: hasPhDData
              ? [{
                title: phdStudy.value.title,
                university: phdStudy.value.university,
                startDate: phdStudy.value.startDate,
                endDate: phdStudy.value.endDate,
                subjects: phdStudy.value.subjects
              }]
              : []
          }
        }
        emit('update', payload)
      }, 250)
    }

    // ---------- ARRAY HELPERS ----------
    const addCollaborator = () => {
      if (collaboratorDraft.value) {
        nextTick(() => {
          if (newCollaboratorDepartmentInput.value) {
            newCollaboratorDepartmentInput.value.focus()
          }
        })
        return
      }

      collaboratorDraft.value = { department: '', institution: '', country: '' }
      collaboratorDraftAttempted.value = false

      nextTick(() => {
        if (newCollaboratorDepartmentInput.value) {
          newCollaboratorDepartmentInput.value.focus()
        }
      })
    }

    const cancelNewCollaborator = () => {
      collaboratorDraft.value = null
      collaboratorDraftAttempted.value = false
    }

    const isCollaboratorFieldInvalid = (item, field, index = null, isDraft = false) => {
      const shouldValidate = isDraft ? collaboratorDraftAttempted.value : Boolean(collaboratorAttempted.value[index])
      if (!shouldValidate) return false
      if (field === 'department') return !String(item?.department || '').trim()
      if (field === 'institution') return !String(item?.institution || '').trim()
      if (field === 'country') return !String(item?.country || '').trim()
      return false
    }

    const handleCollaboratorDraftInput = () => {
      if (collaboratorDraftAttempted.value && isCollaboratorValid(collaboratorDraft.value)) {
        collaboratorDraftAttempted.value = false
      }
    }

    const saveNewCollaborator = () => {
      if (!isCollaboratorValid(collaboratorDraft.value)) {
        collaboratorDraftAttempted.value = true
        return
      }

      collaborators.value.unshift({ ...collaboratorDraft.value })
      expandedCollaborator.value = 0
      collaboratorDraft.value = null
      collaboratorDraftAttempted.value = false
      emitUpdate()
    }

    const markCollaboratorDirty = (index) => {
      collaboratorDirty.value = {
        ...collaboratorDirty.value,
        [index]: true
      }

      if (collaboratorAttempted.value[index] && isCollaboratorValid(collaborators.value[index])) {
        collaboratorAttempted.value = {
          ...collaboratorAttempted.value,
          [index]: false
        }
      }
    }

    const saveCollaborator = (index) => {
      if (!isCollaboratorValid(collaborators.value[index])) {
        collaboratorAttempted.value = {
          ...collaboratorAttempted.value,
          [index]: true
        }
        return
      }

      collaboratorDirty.value = {
        ...collaboratorDirty.value,
        [index]: false
      }
      collaboratorAttempted.value = {
        ...collaboratorAttempted.value,
        [index]: false
      }

      emitUpdate()
    }

    const removeCollaborator = (index) => {
      if (collaborators.value.length > 0) {
        collaborators.value.splice(index, 1)
        const nextDirty = {}
        Object.keys(collaboratorDirty.value).forEach((key) => {
          const dirtyIndex = Number(key)
          if (dirtyIndex < index) {
            nextDirty[dirtyIndex] = collaboratorDirty.value[key]
          }
          if (dirtyIndex > index) {
            nextDirty[dirtyIndex - 1] = collaboratorDirty.value[key]
          }
        })
        collaboratorDirty.value = nextDirty
        const nextAttempted = {}
        Object.keys(collaboratorAttempted.value).forEach((key) => {
          const attemptedIndex = Number(key)
          if (attemptedIndex < index) {
            nextAttempted[attemptedIndex] = collaboratorAttempted.value[key]
          }
          if (attemptedIndex > index) {
            nextAttempted[attemptedIndex - 1] = collaboratorAttempted.value[key]
          }
        })
        collaboratorAttempted.value = nextAttempted
        emitUpdate()
      }
    }

    const addParticipant = () => {
      if (participantDraft.value) {
        nextTick(() => {
          if (newParticipantDepartmentInput.value) {
            newParticipantDepartmentInput.value.focus()
          }
        })
        return
      }

      participantDraft.value = { department: '', institution: '', country: '' }
      participantDraftAttempted.value = false

      nextTick(() => {
        if (newParticipantDepartmentInput.value) {
          newParticipantDepartmentInput.value.focus()
        }
      })
    }

    const cancelNewParticipant = () => {
      participantDraft.value = null
      participantDraftAttempted.value = false
    }

    const isParticipantFieldInvalid = (item, field, index = null, isDraft = false) => {
      const shouldValidate = isDraft ? participantDraftAttempted.value : Boolean(participantAttempted.value[index])
      if (!shouldValidate) return false
      if (field === 'department') return !String(item?.department || '').trim()
      if (field === 'institution') return !String(item?.institution || '').trim()
      if (field === 'country') return !String(item?.country || '').trim()
      return false
    }

    const handleParticipantDraftInput = () => {
      if (participantDraftAttempted.value && isParticipantValid(participantDraft.value)) {
        participantDraftAttempted.value = false
      }
    }

    const saveNewParticipant = () => {
      if (!isParticipantValid(participantDraft.value)) {
        participantDraftAttempted.value = true
        return
      }

      participants.value.unshift({ ...participantDraft.value })
      expandedParticipant.value = 0
      participantDraft.value = null
      participantDraftAttempted.value = false
      emitUpdate()
    }

    const markParticipantDirty = (index) => {
      participantDirty.value = {
        ...participantDirty.value,
        [index]: true
      }

      if (participantAttempted.value[index] && isParticipantValid(participants.value[index])) {
        participantAttempted.value = {
          ...participantAttempted.value,
          [index]: false
        }
      }
    }

    const saveParticipant = (index) => {
      if (!isParticipantValid(participants.value[index])) {
        participantAttempted.value = {
          ...participantAttempted.value,
          [index]: true
        }
        return
      }

      participantDirty.value = {
        ...participantDirty.value,
        [index]: false
      }
      participantAttempted.value = {
        ...participantAttempted.value,
        [index]: false
      }

      emitUpdate()
    }

    const removeParticipant = (index) => {
      if (participants.value.length > 0) {
        participants.value.splice(index, 1)
        const nextDirty = {}
        Object.keys(participantDirty.value).forEach((key) => {
          const dirtyIndex = Number(key)
          if (dirtyIndex < index) {
            nextDirty[dirtyIndex] = participantDirty.value[key]
          }
          if (dirtyIndex > index) {
            nextDirty[dirtyIndex - 1] = participantDirty.value[key]
          }
        })
        participantDirty.value = nextDirty
        const nextAttempted = {}
        Object.keys(participantAttempted.value).forEach((key) => {
          const attemptedIndex = Number(key)
          if (attemptedIndex < index) {
            nextAttempted[attemptedIndex] = participantAttempted.value[key]
          }
          if (attemptedIndex > index) {
            nextAttempted[attemptedIndex - 1] = participantAttempted.value[key]
          }
        })
        participantAttempted.value = nextAttempted
        emitUpdate()
      }
    }

    const addStudent = () => {
      if (studentDraft.value) {
        nextTick(() => {
          if (newStudentTypeInput.value) {
            newStudentTypeInput.value.focus()
          }
        })
        return
      }

      studentDraft.value = {
        type: '', name: '', title: '', country: '', university: '', startDate: '', endDate: ''
      }
      studentDraftAttempted.value = false

      nextTick(() => {
        if (newStudentTypeInput.value) {
          newStudentTypeInput.value.focus()
        }
      })
    }

    const cancelNewStudent = () => {
      studentDraft.value = null
      studentDraftAttempted.value = false
    }

    const isStudentFieldInvalid = (item, field, index = null, isDraft = false) => {
      const shouldValidate = isDraft ? studentDraftAttempted.value : Boolean(studentAttempted.value[index])
      if (!shouldValidate) return false
      if (field === 'type') return !String(item?.type || '').trim()
      if (field === 'name') return !String(item?.name || '').trim()
      if (field === 'title') return !String(item?.title || '').trim()
      if (field === 'country') return !String(item?.country || '').trim()
      if (field === 'university') return !String(item?.university || '').trim()
      if (field === 'startDate') return !String(item?.startDate || '').trim()
      if (field === 'endDate') return !String(item?.endDate || '').trim() || hasInvalidDateRange(item)
      return false
    }

    const handleStudentDraftInput = () => {
      if (studentDraftAttempted.value && isStudentValid(studentDraft.value)) {
        studentDraftAttempted.value = false
      }
    }

    const saveNewStudent = () => {
      if (!isStudentValid(studentDraft.value)) {
        studentDraftAttempted.value = true
        return
      }

      students.value.unshift({ ...studentDraft.value })
      expandedStudent.value = 0
      studentDraft.value = null
      studentDraftAttempted.value = false
      emitUpdate()
    }

    const markStudentDirty = (index) => {
      studentDirty.value = {
        ...studentDirty.value,
        [index]: true
      }

      if (studentAttempted.value[index] && isStudentValid(students.value[index])) {
        studentAttempted.value = {
          ...studentAttempted.value,
          [index]: false
        }
      }
    }

    const saveStudent = (index) => {
      if (!isStudentValid(students.value[index])) {
        studentAttempted.value = {
          ...studentAttempted.value,
          [index]: true
        }
        return
      }

      studentDirty.value = {
        ...studentDirty.value,
        [index]: false
      }
      studentAttempted.value = {
        ...studentAttempted.value,
        [index]: false
      }

      emitUpdate()
    }

    const removeStudent = (index) => {
      if (students.value.length > 0) {
        students.value.splice(index, 1)
        const nextDirty = {}
        Object.keys(studentDirty.value).forEach((key) => {
          const dirtyIndex = Number(key)
          if (dirtyIndex < index) {
            nextDirty[dirtyIndex] = studentDirty.value[key]
          }
          if (dirtyIndex > index) {
            nextDirty[dirtyIndex - 1] = studentDirty.value[key]
          }
        })
        studentDirty.value = nextDirty
        const nextAttempted = {}
        Object.keys(studentAttempted.value).forEach((key) => {
          const attemptedIndex = Number(key)
          if (attemptedIndex < index) {
            nextAttempted[attemptedIndex] = studentAttempted.value[key]
          }
          if (attemptedIndex > index) {
            nextAttempted[attemptedIndex - 1] = studentAttempted.value[key]
          }
        })
        studentAttempted.value = nextAttempted
        emitUpdate()
      }
    }

    const addSite = () => {
      if (siteDraft.value) {
        nextTick(() => {
          if (newSiteNameInput.value) {
            newSiteNameInput.value.focus()
          }
        })
        return
      }

      siteDraft.value = { name: '' }
      siteDraftAttempted.value = false

      nextTick(() => {
        if (newSiteNameInput.value) {
          newSiteNameInput.value.focus()
        }
      })
    }

    const cancelNewSite = () => {
      siteDraft.value = null
      siteDraftAttempted.value = false
    }

    const isSiteFieldInvalid = (item, index = null, isDraft = false) => {
      const shouldValidate = isDraft ? siteDraftAttempted.value : Boolean(siteAttempted.value[index])
      if (!shouldValidate) return false
      return !String(item?.name || '').trim()
    }

    const handleSiteDraftInput = () => {
      if (siteDraftAttempted.value && isSiteValid(siteDraft.value)) {
        siteDraftAttempted.value = false
      }
    }

    const saveNewSite = () => {
      if (!isSiteValid(siteDraft.value)) {
        siteDraftAttempted.value = true
        return
      }

      sites.value.unshift({ ...siteDraft.value })
      expandedSite.value = 0
      siteDraft.value = null
      siteDraftAttempted.value = false
      emitUpdate()
    }

    const markSiteDirty = (index) => {
      siteDirty.value = {
        ...siteDirty.value,
        [index]: true
      }

      if (siteAttempted.value[index] && isSiteValid(sites.value[index])) {
        siteAttempted.value = {
          ...siteAttempted.value,
          [index]: false
        }
      }
    }

    const saveSite = (index) => {
      if (!isSiteValid(sites.value[index])) {
        siteAttempted.value = {
          ...siteAttempted.value,
          [index]: true
        }
        return
      }

      siteDirty.value = {
        ...siteDirty.value,
        [index]: false
      }
      siteAttempted.value = {
        ...siteAttempted.value,
        [index]: false
      }

      emitUpdate()
    }

    const removeSite = (index) => {
      if (sites.value.length > 0) {
        sites.value.splice(index, 1)
        const nextDirty = {}
        Object.keys(siteDirty.value).forEach((key) => {
          const dirtyIndex = Number(key)
          if (dirtyIndex < index) {
            nextDirty[dirtyIndex] = siteDirty.value[key]
          }
          if (dirtyIndex > index) {
            nextDirty[dirtyIndex - 1] = siteDirty.value[key]
          }
        })
        siteDirty.value = nextDirty
        const nextAttempted = {}
        Object.keys(siteAttempted.value).forEach((key) => {
          const attemptedIndex = Number(key)
          if (attemptedIndex < index) {
            nextAttempted[attemptedIndex] = siteAttempted.value[key]
          }
          if (attemptedIndex > index) {
            nextAttempted[attemptedIndex - 1] = siteAttempted.value[key]
          }
        })
        siteAttempted.value = nextAttempted
        emitUpdate()
      }
    }

    const expandedCollaborator = ref(null)
    const expandedParticipant = ref(null)
    const expandedStudent = ref(null)
    const expandedSite = ref(null)

    return {
      fellowship,
      collaborators,
      participants,
      students,
      sites,
      phdStudy,
      internationalCountries,
      africanCountries,
      emitUpdate,
      addCollaborator,
      cancelNewCollaborator,
      saveNewCollaborator,
      handleCollaboratorDraftInput,
      markCollaboratorDirty,
      saveCollaborator,
      removeCollaborator,
      isCollaboratorFieldInvalid,
      addParticipant,
      cancelNewParticipant,
      saveNewParticipant,
      handleParticipantDraftInput,
      markParticipantDirty,
      saveParticipant,
      removeParticipant,
      isParticipantFieldInvalid,
      addStudent,
      cancelNewStudent,
      saveNewStudent,
      handleStudentDraftInput,
      markStudentDirty,
      saveStudent,
      removeStudent,
      isStudentFieldInvalid,
      addSite,
      cancelNewSite,
      saveNewSite,
      handleSiteDraftInput,
      markSiteDirty,
      saveSite,
      removeSite,
      isSiteFieldInvalid,
      markPhdDirty,
      savePhdStudy,
      isPhdFieldInvalid,
      expandedCollaborator,
      expandedParticipant,
      expandedStudent,
      expandedSite,
      collaboratorDraft,
      collaboratorDirty,
      isCollaboratorValid,
      newCollaboratorDepartmentInput,
      participantDraft,
      participantDirty,
      isParticipantValid,
      newParticipantDepartmentInput,
      studentDraft,
      studentDirty,
      isStudentValid,
      newStudentTypeInput,
      siteDraft,
      siteDirty,
      isSiteValid,
      formatCollapsibleHeader,
      formatCollapsibleHeaderWithCountry,
      getYear,
      newSiteNameInput,
      phdDirty,
      isPhdValid
    }
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
  color: #495057;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0;
}

/* Standardized collapsible header styling */
.accordion-button {
  font-weight: var(--font-weight-regular);
  color: #495057;
  padding: 1rem;
  font-size: var(--font-size-base);
}

.accordion-button:not(.collapsed) {
  background-color: #e3f2fd;
  color: #495057;
  border-color: #17a2b8;
}

.accordion-button:hover {
  background-color: #f0f8ff;
  color: #495057;
}
</style>
